import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Phone, Mail, MapPin, X, Star, ChevronDown, ChevronRight, ChevronLeft, Check, Dumbbell, Target, Heart, Zap, Users, Clock, Award, ArrowRight, Play, Sparkles, Shield, TrendingUp, Activity, CheckCircle2, Calendar, ExternalLink, HelpCircle, MessageCircle, Home, Flame, Mountain, Scan, Apple, CircleDot, ParkingCircle, Globe, Monitor, Video, Instagram, Facebook, Linkedin, Gift, LogIn, Trophy, BarChart3, Link2, Share2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import { SEOHead } from '../hooks/usePageSEO';
import { LOGO_URL, STUDIO_IMAGE, LAURENT_IMAGE, TARAH_IMAGE, BOOKING_URL, EMS_SITE_URL, WHATSAPP_URL, SOCIAL_LINKS, IMAGES } from '../data/constants';
import { services } from '../data/services';
import { blogArticles } from '../data/blogData';
import { pricingCategories } from '../data/pricingData';
import { testimonials } from '../data/testimonials';
import { faqItems } from '../data/faqData';
import { AnimatedSection, BackNav, useIntersectionObserver, SectionBadge, AnimatedBadge, ParticlesBackground } from '../components/shared';
import { ServiceCard, ServiceCardWhite } from '../components/ServiceCards';
import { TestimonialsCarousel } from '../components/TestimonialsCarousel';
import { HomeContactSection } from '../components/HomeContactSection';
import { BlogArticleModal } from '../components/BlogArticleModal';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
    consent: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', null
  const { t, language } = useLanguage();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          subject: "",
          message: "",
          consent: false
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error sending contact form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="pt-32" data-testid="contact-page">
      <SEOHead
        title="Contact | Body-Up Personal Training Nyon | Consultation Gratuite"
        description="Contactez Body-Up Personal Training Nyon. Tel: +41 78 604 44 23. Email: info@body-up.ch. Route des Tattes d'Oie 95, 1260 Nyon. Consultation GRATUITE sur rendez-vous."
        path="/contact"
      />
      <section className="py-20 bg-gradient-to-b from-black to-gray-900 min-h-screen relative overflow-hidden">
        {/* Background Glow Effects */}
        <div className="absolute top-20 left-0 w-96 h-96 bg-primary/20 rounded-full blur-3xl opacity-30 animate-pulse-slow" />
        <div className="absolute bottom-20 right-0 w-96 h-96 bg-pink-500/20 rounded-full blur-3xl opacity-30 animate-pulse-slow" style={{ animationDelay: '2s' }} />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <BackNav />
          <AnimatedSection>
            <div className="text-center mb-16">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-primary/20 to-pink-500/20 border border-primary/50 rounded-full text-primary text-sm font-medium mb-4 animate-pulse-slow">
                <Sparkles className="w-4 h-4 animate-spin-slow" />
                📞 {t.contactPage?.subtitle || "Contact"}
                <Sparkles className="w-4 h-4 animate-spin-slow" />
              </span>
              <h1 className="text-4xl md:text-6xl font-bold text-white mt-4">
                {language === 'fr' ? 'Parlons de Vos' : "Let's Talk About Your"} <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-pink-400 animate-gradient">{language === 'fr' ? 'Objectifs' : 'Goals'}</span>
              </h1>
              <p className="text-gray-400 mt-4 max-w-2xl mx-auto text-lg">
                {language === 'fr' ? "Contactez-nous pour planifier votre première consultation gratuite." : "Contact us to schedule your first free consultation."}
              </p>
            </div>
          </AnimatedSection>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form - WAHOU Effect */}
            <AnimatedSection>
              <div className="group relative bg-gradient-to-br from-white/10 via-white/5 to-transparent rounded-2xl p-8 border border-white/20 hover:border-primary/50 transition-all duration-500 overflow-hidden">
                {/* Animated glow */}
                <div className="absolute top-0 right-0 w-48 h-48 bg-primary/20 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="absolute bottom-0 left-0 w-32 h-32 bg-pink-500/20 rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                <div className="relative z-10">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 bg-gradient-to-br from-primary to-pink-500 rounded-xl flex items-center justify-center shadow-lg">
                      <Mail className="w-5 h-5 text-white" />
                    </div>
                    <h2 className="text-2xl font-bold text-white">{t.contactPage?.formTitle || "Envoyez-nous un message"}</h2>
                  </div>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="group/input">
                        <label className="block text-gray-400 text-sm mb-2">{t.contactPage?.firstName || "Prénom"} *</label>
                        <input
                          type="text"
                          value={formData.firstName}
                          onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                          className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-primary focus:bg-white/10 transition-all duration-300 focus:shadow-lg focus:shadow-primary/10"
                          placeholder={language === 'fr' ? "Votre prénom" : "Your first name"}
                          data-testid="input-firstname"
                          required
                        />
                      </div>
                      <div className="group/input">
                        <label className="block text-gray-400 text-sm mb-2">{t.contactPage?.lastName || "Nom"} *</label>
                        <input
                          type="text"
                          value={formData.lastName}
                          onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                          className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-primary focus:bg-white/10 transition-all duration-300 focus:shadow-lg focus:shadow-primary/10"
                          placeholder={language === 'fr' ? "Votre nom" : "Your last name"}
                          data-testid="input-lastname"
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-gray-400 text-sm mb-2">{t.contactPage?.emailLabel || "Email"} *</label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-primary focus:bg-white/10 transition-all duration-300 focus:shadow-lg focus:shadow-primary/10"
                        placeholder={language === 'fr' ? "votre@email.com" : "your@email.com"}
                        data-testid="input-email"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-gray-400 text-sm mb-2">{t.contactPage?.phoneLabel || "Téléphone"}</label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-primary focus:bg-white/10 transition-all duration-300 focus:shadow-lg focus:shadow-primary/10"
                        placeholder="+41 XX XXX XX XX"
                        data-testid="input-phone"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-400 text-sm mb-2">{t.contactPage?.subjectLabel || "Sujet"} *</label>
                      <select
                        value={formData.subject}
                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                        className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-xl text-white focus:outline-none focus:border-primary focus:bg-white/10 transition-all duration-300 focus:shadow-lg focus:shadow-primary/10"
                        required
                        data-testid="select-subject"
                      >
                        <option value="" className="bg-gray-900">{t.contactPage?.subjects?.select || "Sélectionnez un sujet"}</option>
                        <option value="trial" className="bg-gray-900">{t.contactPage?.subjects?.trial || "Consultation gratuite"}</option>
                        <option value="info" className="bg-gray-900">{t.contactPage?.subjects?.info || "Demande d'informations"}</option>
                        <option value="pricing" className="bg-gray-900">{t.contactPage?.subjects?.pricing || "Renseignements tarifs"}</option>
                        <option value="partnership" className="bg-gray-900">{t.contactPage?.subjects?.partnership || "Partenariat"}</option>
                        <option value="other" className="bg-gray-900">{t.contactPage?.subjects?.other || "Autre"}</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-gray-400 text-sm mb-2">{t.contactPage?.messageLabel || "Message"}</label>
                      <textarea
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        rows={4}
                        className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-primary resize-none focus:bg-white/10 transition-all duration-300 focus:shadow-lg focus:shadow-primary/10"
                        placeholder={t.contactPage?.messagePlaceholder || "Décrivez vos objectifs..."}
                        data-testid="textarea-message"
                        required
                      />
                    </div>

                    {/* RGPD Consent Checkbox */}
                    <div className="flex items-start gap-3 p-4 bg-white/5 border border-white/10 rounded-xl">
                      <input
                        type="checkbox"
                        id="consent"
                        checked={formData.consent}
                        onChange={(e) => setFormData({ ...formData, consent: e.target.checked })}
                        className="mt-1 w-4 h-4 accent-primary cursor-pointer"
                        required
                        data-testid="checkbox-consent"
                      />
                      <label htmlFor="consent" className="text-sm text-gray-400 cursor-pointer leading-relaxed">
                        {language === 'fr' 
                          ? "J'accepte que mes données personnelles soient utilisées par Body-Up Personal Training pour répondre à ma demande. Conformément au RGPD, je peux à tout moment demander l'accès, la modification ou la suppression de mes données en contactant " 
                          : "I agree that my personal data may be used by Body-Up Personal Training to respond to my request. In accordance with GDPR, I can request access, modification or deletion of my data at any time by contacting "}
                        <a href="mailto:info@body-up.ch" className="text-primary hover:text-primary/80 underline">info@body-up.ch</a>
                        {language === 'fr' ? ". *" : ". *"}
                      </label>
                    </div>
                    
                    {/* Success Message */}
                    {submitStatus === 'success' && (
                      <div className="p-4 bg-green-500/20 border border-green-500/50 rounded-xl text-green-400 flex items-center gap-3">
                        <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
                        <span>{t.contactPage?.successMessage || "Message envoyé avec succès ! Nous vous répondrons dans les plus brefs délais."}</span>
                      </div>
                    )}
                    
                    {/* Error Message */}
                    {submitStatus === 'error' && (
                      <div className="p-4 bg-red-500/20 border border-red-500/50 rounded-xl text-red-400 flex items-center gap-3">
                        <X className="w-5 h-5 flex-shrink-0" />
                        <span>{t.contactPage?.errorMessage || "Une erreur est survenue. Veuillez réessayer ou nous contacter par téléphone."}</span>
                      </div>
                    )}
                    
                    <Button 
                      type="submit" 
                      disabled={isSubmitting}
                      className="w-full bg-gradient-to-r from-primary to-pink-500 hover:from-primary/90 hover:to-pink-500/90 text-white py-6 hover:scale-[1.02] transition-all duration-300 shadow-lg shadow-primary/30 hover:shadow-primary/50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100" 
                      data-testid="submit-btn"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                          {t.contactPage?.submitting || "Envoi en cours..."}
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          {t.contactPage?.submitButton || "Envoyer le message"}
                        </>
                      )}
                    </Button>
                  </form>
                </div>
              </div>
            </AnimatedSection>

            {/* Contact Info - WAHOU Effect */}
            <AnimatedSection delay={200}>
              <div className="space-y-8">
                {/* Quick Contact - WAHOU Effect */}
                <div className="group relative bg-gradient-to-br from-white/10 via-white/5 to-transparent rounded-2xl p-8 border border-white/20 hover:border-primary/50 transition-all duration-500 overflow-hidden">
                  {/* Animated glow */}
                  <div className="absolute top-0 right-0 w-32 h-32 bg-primary/20 rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  
                  <div className="relative z-10">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-10 h-10 bg-gradient-to-br from-primary to-pink-500 rounded-xl flex items-center justify-center shadow-lg">
                        <Phone className="w-5 h-5 text-white" />
                      </div>
                      <h2 className="text-2xl font-bold text-white">{language === 'fr' ? 'Contact Direct' : 'Direct Contact'}</h2>
                    </div>
                    <div className="space-y-4">
                      <a
                        href="tel:+41786044423"
                        className="group/item flex items-center gap-4 p-4 bg-gradient-to-r from-white/5 to-transparent rounded-xl hover:from-primary/20 hover:to-pink-500/10 transition-all duration-300 border border-transparent hover:border-primary/30"
                      >
                        <div className="w-12 h-12 bg-gradient-to-br from-primary/20 to-pink-500/20 rounded-xl flex items-center justify-center group-hover/item:from-primary/30 group-hover/item:to-pink-500/30 group-hover/item:scale-110 transition-all duration-300">
                          <Phone className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                          <div className="text-gray-400 text-sm">{t.footer?.phone || "Téléphone"}</div>
                          <div className="text-white font-medium group-hover/item:text-primary transition-colors">+41 78 604 44 23</div>
                        </div>
                      </a>
                      <a
                        href={WHATSAPP_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group/item flex items-center gap-4 p-4 bg-gradient-to-r from-green-500/10 to-transparent rounded-xl hover:from-green-500/20 hover:to-green-400/10 transition-all duration-300 border border-green-500/30 hover:border-green-500/50"
                      >
                        <div className="w-12 h-12 bg-gradient-to-br from-green-500/30 to-green-400/20 rounded-xl flex items-center justify-center group-hover/item:from-green-500/40 group-hover/item:to-green-400/30 group-hover/item:scale-110 transition-all duration-300">
                          <svg viewBox="0 0 24 24" className="w-6 h-6 fill-green-500">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                          </svg>
                        </div>
                        <div>
                          <div className="text-gray-400 text-sm">WhatsApp</div>
                          <div className="text-green-400 font-medium group-hover/item:text-green-300 transition-colors">{language === 'fr' ? 'Écrivez-nous !' : 'Message us!'}</div>
                        </div>
                      </a>
                      <a
                        href="mailto:info@body-up.ch"
                        className="group/item flex items-center gap-4 p-4 bg-gradient-to-r from-white/5 to-transparent rounded-xl hover:from-primary/20 hover:to-pink-500/10 transition-all duration-300 border border-transparent hover:border-primary/30"
                      >
                        <div className="w-12 h-12 bg-gradient-to-br from-primary/20 to-pink-500/20 rounded-xl flex items-center justify-center group-hover/item:from-primary/30 group-hover/item:to-pink-500/30 group-hover/item:scale-110 transition-all duration-300">
                          <Mail className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                          <div className="text-gray-400 text-sm">{t.footer?.email || "Email"}</div>
                          <div className="text-white font-medium group-hover/item:text-primary transition-colors">info@body-up.ch</div>
                        </div>
                      </a>
                      <a
                        href="https://maps.google.com/?q=Route+des+Tattes+d'Oie+95,+1260+Nyon"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group/item flex items-center gap-4 p-4 bg-gradient-to-r from-white/5 to-transparent rounded-xl hover:from-primary/20 hover:to-pink-500/10 transition-all duration-300 border border-transparent hover:border-primary/30"
                      >
                        <div className="w-12 h-12 bg-gradient-to-br from-primary/20 to-pink-500/20 rounded-xl flex items-center justify-center group-hover/item:from-primary/30 group-hover/item:to-pink-500/30 group-hover/item:scale-110 transition-all duration-300">
                          <MapPin className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                          <div className="text-gray-400 text-sm">{t.footer?.address || "Adresse"}</div>
                          <div className="text-white font-medium group-hover/item:text-primary transition-colors">Route des Tattes d'Oie 95, 1260 Nyon</div>
                        </div>
                      </a>
                    </div>
                  </div>
                </div>

                {/* Book CTA - WAHOU Effect */}
                <div className="group relative bg-gradient-to-br from-primary/30 via-primary/20 to-pink-500/10 rounded-2xl p-8 border border-primary/50 overflow-hidden hover:shadow-xl hover:shadow-primary/30 transition-all duration-500 hover:-translate-y-1">
                  {/* Animated glow */}
                  <div className="absolute top-0 right-0 w-48 h-48 bg-primary/30 rounded-full blur-3xl opacity-50 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-pink-500/30 rounded-full blur-2xl opacity-50 group-hover:opacity-100 transition-opacity duration-500" />
                  
                  <div className="relative z-10">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-gradient-to-br from-white to-gray-100 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                        <Sparkles className="w-5 h-5 text-primary" />
                      </div>
                      <h3 className="text-xl font-bold text-white">{t.contactPage?.directBooking || "Réservez Directement"}</h3>
                    </div>
                    <p className="text-gray-300 mb-6">
                      {t.contactPage?.directBookingText || "La façon la plus rapide de démarrer ? Réservez votre consultation gratuite en ligne."}
                    </p>
                    <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                      <Button size="lg" className="w-full bg-gradient-to-r from-white to-gray-100 text-primary hover:from-gray-100 hover:to-white hover:scale-[1.02] transition-all duration-300 shadow-lg shadow-white/20">
                        <Calendar className="w-5 h-5 mr-2" />
                        {language === 'fr' ? 'Réserver en ligne' : 'Book online'}
                      </Button>
                    </a>
                  </div>
                </div>

              </div>
            </AnimatedSection>
          </div>

          {/* Parking & Access Section - Full Width */}
          <div className="lg:col-span-2 mt-12">
            <AnimatedSection>
              <div className="text-center mb-10">
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500/20 to-blue-400/10 border border-blue-500/50 rounded-full text-blue-400 text-sm font-medium mb-4">
                  <ParkingCircle className="w-4 h-4" />
                  {t.contactPage?.accessParking || "Accès & Parking"}
                </span>
                <h2 className="text-3xl md:text-4xl font-bold text-white">
                  {t.contactPage?.howToReach || "Comment Nous"} <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-primary">{t.contactPage?.reachHighlight || "Rejoindre"}</span>
                </h2>
              </div>
            </AnimatedSection>

            <div className="grid lg:grid-cols-2 gap-8">
              {/* Map */}
              <AnimatedSection>
                <div className="group relative bg-gradient-to-br from-white/10 via-white/5 to-transparent rounded-2xl overflow-hidden border border-white/20 hover:border-blue-500/50 transition-all duration-500">
                  <div className="aspect-video w-full">
                    <iframe
                      title="Body-Up Location"
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2755.7986!2d6.2275!3d46.3833!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x478c62f7d0000001%3A0x0!2sRoute%20des%20Tattes%20d&#39;Oie%2095%2C%201260%20Nyon!5e0!3m2!1sfr!2sch!4v1700000000000!5m2!1sfr!2sch"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    ></iframe>
                  </div>
                  <div className="p-6">
                    <div className="flex items-start gap-3 mb-4">
                      <MapPin className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                      <div>
                        <h3 className="text-white font-bold">Body-Up Personal Training</h3>
                        <p className="text-gray-400">Route des Tattes d'Oie 95, 1260 Nyon</p>
                      </div>
                    </div>
                    <a
                      href="https://maps.google.com/?q=Route+des+Tattes+d'Oie+95,+1260+Nyon,+Suisse"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-primary hover:text-primary/80 transition-colors"
                    >
                      Ouvrir dans Google Maps
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              </AnimatedSection>

              {/* Parking Options */}
              <AnimatedSection delay={200}>
                <div className="space-y-4">
                  <h3 className="text-xl font-bold text-white flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                      <span className="text-white font-bold">P</span>
                    </div>
                    {t.contactPage?.parkingOptions || "Options de Parking"}
                  </h3>
                  
                  {[
                    { name: "Parking Denner", location: "Chemin des Saules", type: language === 'fr' ? "Visiteurs" : "Visitors", icon: "🅿️" },
                    { name: "Parking -1", location: "Let's Go Fitness", type: language === 'fr' ? "Visiteurs" : "Visitors", icon: "🅿️" },
                    { name: language === 'fr' ? "Parking du Stade" : "Stadium Parking", location: "Route des Tattes d'Oie", type: language === 'fr' ? "Visiteurs" : "Visitors", icon: "⚽" },
                    { name: language === 'fr' ? "Entrée Body-Up" : "Body-Up Entrance", location: "Route des Tattes d'Oie 95", type: language === 'fr' ? "2 places privées" : "2 private spots", icon: "🏋️" },
                    { name: language === 'fr' ? "École Tattes d'Oie" : "Tattes d'Oie School", location: "Chemin d'Eysins", type: language === 'fr' ? "~6 places gratuites 4h" : "~6 free spots 4h", icon: "🏫" },
                    { name: "Route des Tattes d'Oie", location: language === 'fr' ? "Zone bleue" : "Blue zone", type: language === 'fr' ? "Gratuit avec disque" : "Free with disc", icon: "🔵" }
                  ].map((parking, index) => (
                    <div
                      key={index}
                      className="group flex items-start gap-4 p-4 bg-gradient-to-r from-white/5 to-transparent rounded-xl border border-white/10 hover:border-blue-500/30 transition-all hover:-translate-y-1 duration-300"
                    >
                      <span className="text-2xl">{parking.icon}</span>
                      <div className="flex-1">
                        <h4 className="text-white font-semibold text-sm">{parking.name}</h4>
                        <p className="text-gray-500 text-xs">{parking.location}</p>
                      </div>
                      <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs font-medium rounded-full">
                        {parking.type}
                      </span>
                    </div>
                  ))}

                  {/* Access Info */}
                  <div className="mt-6 p-5 bg-gradient-to-br from-blue-500/20 to-blue-500/5 rounded-xl border border-blue-500/30">
                    <h4 className="text-white font-bold mb-3 flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5 text-blue-400" />
                      {t.contactPage?.goodToKnow || "Bon à savoir"}
                    </h4>
                    <ul className="space-y-2 text-gray-300 text-sm">
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-primary flex-shrink-0" />
                        {t.contactPage?.accessibleFrom || "Accessible depuis Gland, Rolle, Coppet"}
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-primary flex-shrink-0" />
                        {t.contactPage?.nearStation || "À 5 min de la gare de Nyon"}
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-primary flex-shrink-0" />
                        {t.contactPage?.busNearby || "Bus à proximité"}
                      </li>
                    </ul>
                  </div>
                </div>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};


export default ContactPage;
